import React, { createRef, useEffect } from 'react'
import './WheelCanvas.scss'
import { clamp } from './utils'

const drawWheel = (canvasRef, data, drawWheelProps) => {
  const QUANTITY = data.length
  /* eslint-disable prefer-const */
  let {
    outerBorderColor,
    outerBorderWidth,
    innerRadius,
    innerBorderColor,
    innerBorderWidth,
    radiusLineColor,
    radiusLineWidth,
    fontSize,
    perpendicularText,
    textDistance,
    sliceBorderColor,
    sliceBorderWidth,
  } = drawWheelProps
  /* eslint-enable prefer-const */

  outerBorderWidth *= 2
  innerBorderWidth *= 2
  radiusLineWidth *= 2
  fontSize *= 2
  const effectiveSliceBorderWidth = sliceBorderColor ? (sliceBorderWidth || 0) * 2 : 0

  const canvas = canvasRef.current
  if (canvas?.getContext('2d')) {
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, 500, 500)
    ctx.strokeStyle = 'transparent'
    ctx.lineWidth = 0
    // ctx.translate(0.5, 0.5)

    const arc = Math.PI / (QUANTITY / 2)
    const startAngle = 0
    const outsideRadius = canvas.width / 2 - 10

    const clampedTextDistance = clamp(0, 100, textDistance)
    const textRadius = (outsideRadius * clampedTextDistance) / 100

    const clampedInsideRadius = clamp(0, 100, innerRadius)
    const insideRadius = (outsideRadius * clampedInsideRadius) / 100

    const centerX = canvas.width / 2
    const centerY = canvas.height / 2

    ctx.font = `bold ${fontSize}px Helvetica, Arial`

    for (let i = 0; i < data.length; i++) {
      const angle = startAngle + i * arc
      const { style } = data[i]
      ctx.fillStyle = style && style.backgroundColor

      ctx.beginPath()
      ctx.arc(centerX, centerY, outsideRadius, angle, angle + arc, false)
      ctx.arc(centerX, centerY, insideRadius, angle + arc, angle, true)
      ctx.closePath()
      ctx.fill()
      if (effectiveSliceBorderWidth > 0) {
        ctx.save()
        ctx.strokeStyle = sliceBorderColor
        ctx.lineWidth = effectiveSliceBorderWidth
        ctx.lineJoin = 'round'
        ctx.stroke()
        ctx.restore()
      } else {
        ctx.strokeStyle = 'transparent'
        ctx.lineWidth = 0
        ctx.stroke()
      }

      ctx.save()

      // WHEEL RADIUS LINES
      ctx.strokeStyle = radiusLineWidth <= 0 ? 'transparent' : radiusLineColor
      ctx.lineWidth = radiusLineWidth
      for (let j = 0; j < data.length; j++) {
        const radiusAngle = startAngle + j * arc
        ctx.beginPath()
        ctx.moveTo(
          centerX + (insideRadius + 1) * Math.cos(radiusAngle),
          centerY + (insideRadius + 1) * Math.sin(radiusAngle),
        )
        ctx.lineTo(
          centerX + (outsideRadius - 1) * Math.cos(radiusAngle),
          centerY + (outsideRadius - 1) * Math.sin(radiusAngle),
        )
        ctx.closePath()
        ctx.stroke()
      }

      // WHEEL OUTER BORDER
      ctx.strokeStyle = outerBorderWidth <= 0 ? 'transparent' : outerBorderColor
      ctx.lineWidth = outerBorderWidth
      ctx.beginPath()
      ctx.arc(
        centerX,
        centerY,
        outsideRadius - ctx.lineWidth / 2,
        0,
        2 * Math.PI,
      )
      ctx.closePath()
      ctx.stroke()

      // WHEEL INNER BORDER
      ctx.strokeStyle = innerBorderWidth <= 0 ? 'transparent' : innerBorderColor
      ctx.lineWidth = innerBorderWidth
      ctx.beginPath()
      ctx.arc(
        centerX,
        centerY,
        insideRadius + ctx.lineWidth / 2 - 1,
        0,
        2 * Math.PI,
      )
      ctx.closePath()
      ctx.stroke()

      // TEXT FILL
      ctx.fillStyle = style && style.textColor
      const text = data[i].option
      // Per-slice fontSize override (already doubled at top, so double the override too)
      const sliceFontSize = style && style.fontSize ? style.fontSize * 2 : fontSize
      ctx.font = `bold ${sliceFontSize}px Helvetica, Arial`

      if (perpendicularText) {
        // Perpendicular: text along the arc, positioned at textRadius
        ctx.translate(
          centerX + Math.cos(angle + arc / 2) * textRadius,
          centerY + Math.sin(angle + arc / 2) * textRadius,
        )
        ctx.rotate(angle + arc / 2 + Math.PI / 2)
        ctx.fillText(text, -ctx.measureText(text).width / 2, sliceFontSize / 2.7)
      } else {
        // Radial/vertical: text along the radius, centered in usable slice area
        // Account for inner border so text doesn't overlap center icon
        const effectiveInner = insideRadius + innerBorderWidth + 10
        const effectiveOuter = outsideRadius - outerBorderWidth - 10
        const midRadius = (effectiveInner + effectiveOuter) / 2

        ctx.translate(
          centerX + Math.cos(angle + arc / 2) * midRadius,
          centerY + Math.sin(angle + arc / 2) * midRadius,
        )
        ctx.rotate(angle + arc / 2)
        // Use textBaseline middle so text is centered on the radius line (no y drift)
        ctx.textBaseline = 'middle'
        ctx.fillText(text, -ctx.measureText(text).width / 2, 0)
        ctx.textBaseline = 'alphabetic'
      }
      ctx.restore()
    }
  }
}

const WheelCanvas = ({
  width,
  height,
  data,
  outerBorderColor,
  outerBorderWidth,
  innerRadius,
  innerBorderColor,
  innerBorderWidth,
  radiusLineColor,
  radiusLineWidth,
  fontSize,
  perpendicularText,
  textDistance,
  sliceBorderColor,
  sliceBorderWidth,
}) => {
  const canvasRef = createRef()
  const drawWheelProps = {
    outerBorderColor,
    outerBorderWidth,
    innerRadius,
    innerBorderColor,
    innerBorderWidth,
    radiusLineColor,
    radiusLineWidth,
    fontSize,
    perpendicularText,
    textDistance,
    sliceBorderColor,
    sliceBorderWidth,
  }

  useEffect(() => {
    drawWheel(canvasRef, data, drawWheelProps)
  }, [canvasRef, data, drawWheelProps])

  return (
    <canvas className="canvas" ref={canvasRef} width={width} height={height} />
  )
}

export default WheelCanvas
